import { TTransactionGeneralLedgerCode } from '@/context/types/Business';
import { EnumValues } from './EnumValues';
import { SortDirectionEnum } from '@/features/home/types/TransactionHistory';

/*****************
 * TRANSACTIONS
 *
 *****************/

interface ITransaction {
  id: string;
  date: Date;
  walletId: string;
  sign: EnumValues<typeof TransactionSignEnum>;
  title: string;
  balance: number;
  hasAttachments?: boolean;
  generalLedgerCode?: TTransactionGeneralLedgerCode;
  customer?: TCustomerDetails;
}

export const TransactionSignEnum = {
  Debit: 'dr',
  Credit: 'cr'
} as const;

export const TransactionDataTypeEnum = {
  Incoming: 'INCOMING',
  Outgoing: 'OUTGOING'
} as const;

export const TransferStateEnum = {
  Pending: 'pending',
  Cancelled: 'cancelled',
  Completed: 'completed'
} as const;

export interface ITransferTransaction extends ITransaction {
  id: string;
  date: Date;
  walletId: string;
  otherWalletId: string;
  balance: number;
  sign: EnumValues<typeof TransactionSignEnum>;
  amount: number;
  state: EnumValues<typeof TransferStateEnum>;
  title: string;
  description: string;
  hasAttachments?: boolean;
  generalLedgerCode?: TTransactionGeneralLedgerCode;
  customer?: TCustomerDetails;
}

export const PurchaseStateEnum = {
  PreAuth: 'pre_auth',
  Completed: 'completed',
  Declined: 'declined'
} as const;

export type TPurchaseCategory = {
  id: string;
  displayName: string;
  icon: string;
};

export type TPurchasePlace = {
  id: string;
  icon: string;
  baseColor: string;
};

export const PurchaseSourceEnum = {
  ATM: 'atm',
  POS: 'pos',
  ONLINE: 'online',
  OTHER: 'other'
} as const;

export const ReceiptExemptionReasonEnum = {
  NoReceiptGiven: 'no_receipt_given',
  LostReceipt: 'lost_receipt',
  DamagedOrFadedReceipt: 'damaged_or_faded_receipt'
} as const;

export interface IPurchaseTransaction extends ITransaction {
  id: string;
  date: Date;
  walletId: string;
  cardId: string;
  balance: number;
  sign: EnumValues<typeof TransactionSignEnum>;
  generalLedgerCode?: TTransactionGeneralLedgerCode;
  customer?: TCustomerDetails;
  txnAmount: number;
  txnCurrency: number;
  billingAmount: number;
  billingCurrency: string;
  state: EnumValues<typeof PurchaseStateEnum>;
  title: string;
  type: EnumValues<typeof TransactionDataTypeEnum>;
  category: TPurchaseCategory;
  place?: TPurchasePlace;
  declinedCode: string;
  source: EnumValues<typeof PurchaseSourceEnum>;
  hasAttachments: boolean;
  isReceiptRequired: boolean;
  receiptExemptionReason?: EnumValues<typeof ReceiptExemptionReasonEnum>;
}

export const AdminTransactionStateEnum = {
  Pending: 'pending',
  Cancelled: 'cancelled',
  Completed: 'completed'
} as const;

export interface IAdminTransaction extends ITransaction {
  id: string;
  date: Date;
  walletId: string;
  balance: number;
  sign: EnumValues<typeof TransactionSignEnum>;
  amount: number;
  state: EnumValues<typeof AdminTransactionStateEnum>;
  title: string;
  hasAttachments?: boolean;
  generalLedgerCode?: TTransactionGeneralLedgerCode;
  customer?: TCustomerDetails;
}

export const BankTransactionStateEnum = {
  Pending: 'pending',
  Processing: 'processing',
  Rejected: 'rejected',
  Completed: 'completed'
} as const;

export const BankTransactionInputSourceEnum = {
  AutomaticPayment: 'AP',
  ATM: 'AT',
  BILL_PAYMENT: 'BP',
  DIRECT_CREDIT: 'DC',
  DIRECT_DEBIT: 'DD',
  ELECTRONIC_DISHONOUR: 'ED',
  FOREIGN_EXCHANGE: 'FX',
  MONEY_MARKET_SYSTEM: 'MM',
  MICR_ENTRY: 'MN',
  POINT_OF_SALE: 'PS',
  SOCIAL_WELFARE_PAYMENT: 'SW'
} as const;

export interface IBankTransaction extends ITransaction {
  id: string;
  date: Date;
  walletId: string;
  balance: number;
  amount: number;
  sign: EnumValues<typeof TransactionSignEnum>;
  state: EnumValues<typeof BankTransactionStateEnum>;
  particulars: string;
  reference: string;
  analysis: string;
  externalAccountNumber: string;
  externalName: string;
  inputSource: EnumValues<typeof BankTransactionInputSourceEnum>;
  title: string;
  type: EnumValues<typeof TransactionDataTypeEnum>;
  hasAttachments?: boolean;
  generalLedgerCode?: TTransactionGeneralLedgerCode;
  customer?: TCustomerDetails;
}

const TopUpTransactionState = {
  PENDING: 'pending',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed'
} as const;

export interface ITopUpTransaction extends ITransaction {
  id: string;
  date: Date;
  walletId: string;
  balance: number;
  amount: number;
  sign: EnumValues<typeof TransactionSignEnum>;
  state: EnumValues<typeof TopUpTransactionState>;
  title: string;
  hasAttachments?: boolean;
  generalLedgerCode?: TTransactionGeneralLedgerCode;
  customer?: TCustomerDetails;
}

export const ProcessingFeeHandlingTypeEnum = {
  Customer: 'customer',
  Merchant: 'merchant'
} as const;
export type ProcessingFeeHandlingType = EnumValues<typeof ProcessingFeeHandlingTypeEnum>;

export interface IEmergePayTransaction extends ITransaction {
  id: string;
  date: Date;
  walletId: string;
  balance: number;
  amount: number;
  sign: EnumValues<typeof TransactionSignEnum>;
  state: EnumValues<typeof TopUpTransactionState>;
  title: string;
  txnId: string;
  processingFee: number;
  processingFeeHandlingType: ProcessingFeeHandlingType;
  hasAttachments?: boolean;
  generalLedgerCode?: TTransactionGeneralLedgerCode;
  customer?: TCustomerDetails;
}

export const TransactionTypeEnum = {
  Admin: 'admin',
  Bank: 'bank',
  Transfer: 'transfer',
  Purchase: 'purchase',
  TopUp: 'top_up',
  EmergePay: 'emerge_pay',
  Tax: 'tax',
  Interest: 'interest',
  Return: 'returns',
  Investment: 'investment'
} as const;

export type TransactionTypes = EnumValues<typeof TransactionTypeEnum>;
export type TransactionData =
  | IPurchaseTransaction
  | IBankTransaction
  | IAdminTransaction
  | ITransferTransaction
  | ITopUpTransaction;

export type TGetTransactionsData = {
  type: TransactionTypes;
  data: TransactionData;
};

/*********************
 * TRANSACTION DETAILS
 *
 *********************/

export type TCustomerDetails = {
  customerId: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  avatar: string;
};

export type TGetTransactionDetailsData = {
  transactionId: string;
  walletId: string;
  customer?: TCustomerDetails;
  attachments: TAttachment[];
  generalLedgerCode?: TTransactionGeneralLedgerCode;
  note?: string;
  readOnly: boolean;
};

export type TAttachment = {
  id: string;
  type: EnumValues<typeof TAttachmentType>;
  url: string;
  state: EnumValues<typeof TAttachmentState>;
  name: string;
};

export const TAttachmentType = {
  Image: 'image',
  Pdf: 'pdf'
} as const;

export const TAttachmentState = {
  Clean: 'clean',
  Dirty: 'dirty',
  Unscanned: 'unscanned'
} as const;

export type TCreateOrUpdateReceiptExemptionRequestData = {
  receiptExemptionReason: EnumValues<typeof ReceiptExemptionReasonEnum>;
};

export type TCreateOrUpdateTransactionNoteRequestData = {
  note: string;
};

export const TransactionHistorySortByFieldEnum = {
  Date: 'date'
} as const;
export type TransactionHistorySortByField = EnumValues<typeof TransactionHistorySortByFieldEnum>;

export type SortDirection = EnumValues<typeof SortDirectionEnum>;

export type TGetBusinessOrAccountTransactionsRequestParams = {
  startDate?: string;
  endDate?: string;
  walletIds?: string[];
  customerIds?: string[];
  transactionTypes?: string[];
  bankTransactionTypes?: BankTransactionType[];
  search?: string;
  sortByField?: TransactionHistorySortByField;
  sortByDirection?: SortDirection;
  page: number;
  size: number;
};

export type TExportTransactionsRequestBody = {
  startDate?: string;
  endDate?: string;
  walletIds?: string[];
  customerIds?: string[];
  transactionTypes?: string[];
  bankTransactionTypes?: BankTransactionType[];
  search?: string;
  sortByField?: TransactionHistorySortByField;
  sortByDirection?: SortDirection;
};

export type BankTransactionType =
  (typeof TransactionDataTypeEnum)[keyof typeof TransactionDataTypeEnum];

export type TTransactionListPaginatedResponse = {
  transactions: TGetTransactionsData[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
};

export type TExportTransactionResponse = {
  fileName: string;
  fileUrl: string;
};
